import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { qk } from '@/lib/queryKeys.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, DateInput, FormField, Textarea } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { centsToRupees, rupeesToCents } from '@/lib/format.js';

export default function EmployeeFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: emp } = useQuery({ queryKey: ['employee', id], queryFn: () => api.get(`/employees/${id}`), enabled: isEdit });
  const { data: depts = [] } = useQuery({ queryKey: ['departments'], queryFn: () => api.get('/departments') });
  const { data: desigs = [] } = useQuery({ queryKey: ['designations'], queryFn: () => api.get('/designations') });
  const { data: grades = [] } = useQuery({ queryKey: ['payGrades'], queryFn: () => api.get('/pay-grades') });
  const { data: shifts = [] } = useQuery({ queryKey: qk.shifts, queryFn: () => api.get('/shifts') });
  const { data: empCategories = [] } = useQuery({ queryKey: qk.employeeCategories, queryFn: () => api.get('/employee-categories') });

  const form = useForm({
    defaultValues: { employment_type: 'permanent', status: 'active', basic_salary: 0 },
  });

  useEffect(() => {
    if (isEdit && emp) {
      form.reset({
        ...emp,
        basic_salary: centsToRupees(emp.basic_salary_cents),
      });
    }
  }, [emp]); // eslint-disable-line

  // Auto-select lone shift for new employees
  useEffect(() => {
    if (!isEdit && shifts.length === 1 && !form.getValues('shift_id')) {
      form.setValue('shift_id', shifts[0].id);
    }
  }, [shifts]); // eslint-disable-line

  const save = useMutation({
    mutationFn: (v) => {
      const payload = {
        emp_no: v.emp_no, full_name: v.full_name, nic: v.nic || null, dob: v.dob || null,
        gender: v.gender || null, contact: v.contact || null, email: v.email || null, address: v.address || null,
        dept_id: v.dept_id ? Number(v.dept_id) : null,
        designation_id: v.designation_id ? Number(v.designation_id) : null,
        grade_id: v.grade_id ? Number(v.grade_id) : null,
        join_date: v.join_date || null, leave_date: v.leave_date || null,
        employment_type: v.employment_type,
        basic_salary_cents: rupeesToCents(v.basic_salary),
        bank_name: v.bank_name || null, bank_branch: v.bank_branch || null, bank_account: v.bank_account || null,
        epf_member_no: v.epf_member_no || null, status: v.status,
        shift_id: v.shift_id ?? null,
        employee_category_id: v.employee_category_id ?? null,
      };
      return isEdit ? api.put(`/employees/${id}`, payload) : api.post('/employees', payload);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['employees', {}] });
      qc.invalidateQueries({ queryKey: ['employee', id] });
      toast({ variant: 'success', title: isEdit ? 'Updated' : 'Created' });
      navigate(`/employees/${res.id || id}`);
    },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  return (
    <div className="space-y-5">
      <PageHeader title={isEdit ? `Edit Employee` : 'New Employee'} actions={
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
      } />

      <form onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <Tabs defaultValue="personal">
          <TabsList>
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="employment">Employment</TabsTrigger>
            <TabsTrigger value="bank">Bank & Statutory</TabsTrigger>            <TabsTrigger value="hr">HR Policies</TabsTrigger>          </TabsList>

          <TabsContent value="personal">
            <Section title="Personal Details">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Employee No" required><Input {...form.register('emp_no', { required: true })} /></FormField>
                <FormField label="Full Name" required><Input {...form.register('full_name', { required: true })} /></FormField>
                <FormField label="NIC"><Input {...form.register('nic')} /></FormField>
                <FormField label="Date of Birth">
                  <Controller name="dob" control={form.control}
                    render={({ field }) => <DateInput value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} />}
                  />
                </FormField>
                <FormField label="Gender">
                  <Select {...form.register('gender')}>
                    <option value="">— select —</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Select>
                </FormField>
                <FormField label="Contact"><Input {...form.register('contact')} /></FormField>
                <FormField label="Email" className="md:col-span-2"><Input type="email" {...form.register('email')} /></FormField>
                <FormField label="Address" className="md:col-span-2"><Textarea {...form.register('address')} /></FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="employment">
            <Section title="Employment">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Department">
                  <Select {...form.register('dept_id')}>
                    <option value="">— none —</option>
                    {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormField>
                <FormField label="Designation">
                  <Select {...form.register('designation_id')}>
                    <option value="">— none —</option>
                    {desigs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormField>
                <FormField label="Pay Grade">
                  <Select {...form.register('grade_id')}>
                    <option value="">— none —</option>
                    {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </Select>
                </FormField>
                <FormField label="Employment Type">
                  <Select {...form.register('employment_type')}>
                    <option value="permanent">Permanent</option>
                    <option value="contract">Contract</option>
                    <option value="probation">Probation</option>
                    <option value="intern">Intern</option>
                  </Select>
                </FormField>
                <FormField label="Join Date">
                  <Controller name="join_date" control={form.control}
                    render={({ field }) => <DateInput value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} />}
                  />
                </FormField>
                <FormField label="Leave Date">
                  <Controller name="leave_date" control={form.control}
                    render={({ field }) => <DateInput value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} />}
                  />
                </FormField>
                <FormField label="Basic Salary (LKR)" required>
                  <Input type="number" step="0.01" {...form.register('basic_salary', { required: true, valueAsNumber: true })} />
                </FormField>
                <FormField label="Status">
                  <Select {...form.register('status')}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="terminated">Terminated</option>
                  </Select>
                </FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="bank">
            <Section title="Bank & Statutory">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Bank Name"><Input {...form.register('bank_name')} /></FormField>
                <FormField label="Branch"><Input {...form.register('bank_branch')} /></FormField>
                <FormField label="Account No"><Input {...form.register('bank_account')} /></FormField>
                <FormField label="EPF Member No"><Input {...form.register('epf_member_no')} /></FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="hr">
            <Section title="HR Policies">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Shift">
                  <Controller
                    control={form.control}
                    name="shift_id"
                    render={({ field }) => (
                      <Select
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.onBlur}
                      >
                        <option value="">— none —</option>
                        {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </Select>
                    )}
                  />
                </FormField>
                <FormField label="Employee Category">
                  <Controller
                    control={form.control}
                    name="employee_category_id"
                    render={({ field }) => (
                      <Select
                        name={field.name}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        onBlur={field.onBlur}
                      >
                        <option value="">— none —</option>
                        {empCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Select>
                    )}
                  />
                </FormField>
              </div>
            </Section>
          </TabsContent>
        </Tabs>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Employee'}</Button>
        </div>
      </form>
    </div>
  );
}
